var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeBreakpointIndexEnum } from '@inter-ikea-kompis/enums';
import { ThemeBreakpointObserver } from '@inter-ikea-kompis/themes';
import { IconPill } from '@inter-ikea-kompis/component-icon-pill';
import MiniSurveyLayoutEnum from '../../enums/MiniSurveyLayoutEnum.js';
import MiniSurveyStateEnum from '../../enums/MiniSurveyStateEnum.js';
import MiniSurveyCard from '../mini-survey-card/MiniSurveyCard.js';
import MiniSurveyStyle from './MiniSurveyStyle.js';
/**
 * Mini Survey component.
 *
 * @event close Click on close button
 * @event select Emitted when user selects a smiley
 * @event reset Emitted when component should reset
 */
export default class MiniSurvey extends KompisElement {
    constructor() {
        super(...arguments);
        // The layout of the Mini Survey, can be set to grade or binary
        this.layout = MiniSurveyLayoutEnum.grade;
        // Hide or show mini survey with transitions.
        this.visible = false;
        // Valid states can be found in MiniSurveyStateEnum
        this.state = MiniSurveyStateEnum.default;
        // Localized information. Deprecated - use "translations" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // Z-index of the container.
        this.zIndex = 500;
        // Position from top in pixels
        this.top = null;
        // Position from left in pixels
        this.left = null;
        // Position from right in pixels
        this.right = null;
        this.breakpointObserver = new ThemeBreakpointObserver(() => this.requestUpdate());
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        // We want translations  to be required, but since the props to provide translations is optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslations()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        const breakpointIndex = this.breakpointObserver.getBreakpointIndex();
        const isSmallDevice = !!breakpointIndex && breakpointIndex <= ThemeBreakpointIndexEnum.small;
        return html `
			<style>
				${MiniSurveyStyle.getStyle({
            theme: this.theme,
            visible: this.visible,
            zIndex: this.zIndex,
            state: this.state,
            isSmallDevice: isSmallDevice,
            top: this.top,
            left: this.left,
            right: this.right
        })}
			</style>
			<div class="container">${this.getContainer()}</div>
		`;
    }
    connectedCallback() {
        this.customElements.define('kompis-icon-pill', IconPill);
        this.customElements.define('kompis-mini-survey-card', MiniSurveyCard);
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.breakpointObserver.disconnect();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('theme')) {
            this.breakpointObserver.observe(this.theme);
        }
        return true;
    }
    hasTranslations() {
        return !!this.translations || !!this.localizedInformation?.translations;
    }
    /**
     * Gets the select element.
     *
     * @returns Template.
     */
    getContainer() {
        if (!this.visible) {
            return null;
        }
        return html `
			<kompis-mini-survey-card
				.theme="${this.theme}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.questionText="${this.questionText}"
				.state="${this.state}"
				.layout="${this.layout}"
				@close="${this.onCloseClick.bind(this)}"
				@select="${this.onSelectClick.bind(this)}"
				@reset="${() => this.dispatchEvent(new CustomEvent('reset', {
            bubbles: true,
            detail: {
                visible: false,
                state: MiniSurveyStateEnum.default
            }
        }))}"
			>
			</kompis-mini-survey-card>
		`;
    }
    /**
     * Handler for close button.
     */
    onCloseClick() {
        this.dispatchEvent(new CustomEvent('close', {
            bubbles: true,
            detail: {
                type: 'icon-button',
                visible: false
            }
        }));
    }
    /**
     * Handler for select icon pill.
     *
     * @param event Event.
     */
    onSelectClick(event) {
        if (this.layout === MiniSurveyLayoutEnum.grade) {
            this.dispatchEvent(new CustomEvent('select', {
                bubbles: true,
                detail: {
                    type: 'icon-pill',
                    grade: event.detail.grade,
                    state: MiniSurveyStateEnum.success
                }
            }));
        }
        else {
            this.dispatchEvent(new CustomEvent('select', {
                bubbles: true,
                detail: {
                    type: 'click',
                    answer: event.detail.answer,
                    state: MiniSurveyStateEnum.success
                }
            }));
        }
    }
}
__decorate([
    property({ type: Object, required: true })
], MiniSurvey.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], MiniSurvey.prototype, "layout", void 0);
__decorate([
    property({ type: Boolean })
], MiniSurvey.prototype, "visible", void 0);
__decorate([
    property({ type: String })
], MiniSurvey.prototype, "questionText", void 0);
__decorate([
    property({ type: String })
], MiniSurvey.prototype, "state", void 0);
__decorate([
    property({ type: Object })
], MiniSurvey.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], MiniSurvey.prototype, "translations", void 0);
__decorate([
    property({ type: Number })
], MiniSurvey.prototype, "zIndex", void 0);
__decorate([
    property({ type: Number })
], MiniSurvey.prototype, "top", void 0);
__decorate([
    property({ type: Number })
], MiniSurvey.prototype, "left", void 0);
__decorate([
    property({ type: Number })
], MiniSurvey.prototype, "right", void 0);
//# sourceMappingURL=MiniSurvey.js.map